import { ErrorBoundary } from '@datadog/browser-rum-react';
// import { BrowserRouter } from '@rocketlane/rocket-router';
import { QueryClientProvider } from '@tanstack/react-query';
import moment from 'moment';
import 'moment/locale/de';
import 'moment/locale/es';
import 'moment/locale/fi';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/ja';
import 'moment/locale/nl';
import 'moment/locale/pt';
import 'moment/locale/sv';
import { useEffect, useLayoutEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { UserAPI } from 'api/user';
import { AntdLocaleProvider } from 'components/atoms/antd-locale';
import { LocaleIntlProvider } from 'components/i18n/LocaleIntlProvider';
import { AppUpgradeConfirmation } from 'components/molecules/app-update-confirmation';
import { AppCrashIntercom } from 'components/organisms/app-crash/intercom';
import { GlobalStyle } from 'components/styles/global';
import { theme } from 'components/styles/theme';
import { APP_CRASH_KEY, POTENTIAL_APP_CRASH_KEY } from 'constant';
import { LANGUAGE_SELECT } from 'constant/constant';
import { AntdRlUISwitcherProvider } from 'contexts/antd-rlui-switcher';
import { CustomerPortalPreviewProvider } from 'contexts/providers';
import { CustomScroll, OsList } from 'customScroll';
import { getCurrentUserProfileState } from 'data/current-user-profile';
import { getBrowserLocale } from 'hooks/use-language-select';
import { OTAUpdatesComponent } from 'ota-updates';
import { OzoneFonts } from 'ozone/atoms';
import { queryClient } from 'queries';
import { logger } from 'services';
import { SetupOkta } from 'setupokta';
import { localeData } from 'translations/en-moment';
import {
  captureChunkErrorTelemetry,
  isChunkLoadError,
  markChunkErrorReloadAttempt,
  reloadAfterChunkError,
  reportChunkErrorReloadOutcome,
  stripCacheBusterParam,
  wasRecentlyReloadedForChunkError,
} from 'utils/chunk-error';
import { getDevEnv } from 'utils/is-localhost';
import { lazyWithRetry } from 'utils/lazyWithRetry';
import { db } from 'workers';
import { store } from './data';

const LazyReactQueryDevtools = lazyWithRetry(() =>
  // eslint-disable-next-line import/no-extraneous-dependencies
  import('@tanstack/react-query-devtools').then(({ ReactQueryDevtools }) => ({
    default: ReactQueryDevtools,
  })),
);

export const App = () => {
  const [locale, setLocale] = useState(
    (typeof window !== 'undefined'
      ? localStorage.getItem(LANGUAGE_SELECT)
      : null) ||
      getBrowserLocale() ||
      'en',
  );

  const { isLocal, isCypress } = getDevEnv();
  const isTesting = process.env.PLAYWRIGHT_TESTS;
  // const navigation = useNavigation();

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const state = store.getState();
      const userProfile = getCurrentUserProfileState(state);
      const userLocale =
        userProfile?.data?.user?.emailLanguagePreference?.toLowerCase?.();

      if (userLocale) {
        setLocale((prevLocale) => {
          if (prevLocale !== userLocale) {
            localStorage.setItem(LANGUAGE_SELECT, userLocale);

            return userLocale;
          }

          return prevLocale;
        });
      }
    });

    // Also check initial state
    const initialState = store.getState();
    const userProfile = getCurrentUserProfileState(initialState);
    const userLocale =
      userProfile?.data?.user?.emailLanguagePreference?.toLowerCase?.();

    if (userLocale) {
      setLocale((prevLocale) => {
        if (prevLocale !== userLocale) {
          localStorage.setItem(LANGUAGE_SELECT, userLocale);

          return userLocale;
        }

        return prevLocale;
      });
    }

    return unsubscribe;
  }, []);

  // Fallback: fetch profile on mount if user data is not available yet
  useEffect(() => {
    const state = store.getState();
    const userProfile = getCurrentUserProfileState(state);

    if (!userProfile?.data?.user) {
      UserAPI.getProfile()
        .then((d) => {
          const userLocale = d?.user?.emailLanguagePreference?.toLowerCase?.();

          if (userLocale) {
            setLocale(userLocale);
            localStorage.setItem(LANGUAGE_SELECT, userLocale);
          }
        })
        .catch(() => {
          // User is not authenticated yet (e.g. on /activate or /login pages)
        });
    }
  }, []);

  useLayoutEffect(() => {
    const normalized = locale?.toLowerCase?.();
    const baseLang = normalized?.split?.('-')?.[0];

    if (baseLang === 'en') {
      moment.updateLocale('en', localeData);
    }
    moment.locale(baseLang);
  }, [locale]);

  useEffect(() => {
    stripCacheBusterParam();
    // If the previous page navigated here via reloadAfterChunkError, the
    // current boot is the first observable evidence the recovery worked
    // (the previous LogRocket session is destroyed by location.replace,
    // so we couldn't see the recovery from there). Track via the new
    // session so replays show post-reload outcomes.
    reportChunkErrorReloadOutcome();
  }, []);

  const AppUpgradeConfirmationComponent = () => (
    <QueryClientProvider client={queryClient}>
      <LocaleIntlProvider defaultLocale="en">
        <AppUpgradeConfirmation />
      </LocaleIntlProvider>
    </QueryClientProvider>
  );

  const fallback = ({
    error,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    resetError,
  }: {
    error: Error;
    resetError: () => void;
  }) => {
    /**
     * Handling errors from indexDb here
     * VersionError occurs on an existing window when the indexDb version is downgraded.
     * This causes the existing tab to break its connection, and the app crashes.
     * Trying to mitigate this by reloading
     * DatabaseClosedError occurs on an existing window when a new window upgrades indexDB version
     */
    if (error?.message?.includes('VersionError')) {
      logger.error(`${POTENTIAL_APP_CRASH_KEY}: ${error?.message}`, {
        error,
      });
      db.delete();
      window.location.reload();

      return <></>;
    }

    // need to check if DatabaseClosedError occurs other than when upgrading
    if (error?.message?.includes('DatabaseClosedError')) {
      logger.error(`${POTENTIAL_APP_CRASH_KEY}: ${error?.message}`, {
        error,
      });

      return <></>;
    }

    if (isChunkLoadError(error)) {
      const justReloaded = wasRecentlyReloadedForChunkError();

      if (!justReloaded) {
        // Auto-recovery path: about to reload. Suppress the Datadog/RUM
        // error log here — the `chunk-error-reload-outcome` LogRocket track
        // event captures the recovery in the next session, and the terminal
        // "already reloaded" branch below still logs the actionable
        // failures. (PR #17601 follow-up.)
        markChunkErrorReloadAttempt();
        reloadAfterChunkError(undefined, {
          name: (error as Error)?.name,
          message: (error as Error)?.message,
        });

        return <></>;
      }

      // Terminal path: auto-reload happened, chunk still failing. Log with
      // the diagnostic telemetry for filterable analysis.
      const telemetry = captureChunkErrorTelemetry(error);

      logger.error(`${APP_CRASH_KEY}: ChunkLoadError - already reloaded`, {
        error,
        ...telemetry,
      });
    }

    if (error) {
      logger.error(`${APP_CRASH_KEY}: ${error.message}`, {
        error,
      });

      return (
        <LocaleIntlProvider defaultLocale="en">
          <QueryClientProvider client={queryClient}>
            <Provider store={store}>
              <AppCrashIntercom />
            </Provider>
          </QueryClientProvider>
        </LocaleIntlProvider>
      );
    }

    return <></>;
  };

  return (
    <ErrorBoundary fallback={fallback}>
      <HelmetProvider>
        <CustomerPortalPreviewProvider>
          <QueryClientProvider client={queryClient}>
            <Provider store={store}>
              <AntdRlUISwitcherProvider>
                <ThemeProvider theme={theme}>
                  {/* <BrowserRouter>  */}
                  <LocaleIntlProvider defaultLocale="en">
                    <AntdLocaleProvider>
                      <SetupOkta />
                      <CustomScroll blacklist={[OsList.Mac]} />
                      <OzoneFonts />
                      <OTAUpdatesComponent />
                    </AntdLocaleProvider>
                  </LocaleIntlProvider>
                  <GlobalStyle />
                  {/* </BrowserRouter> */}
                </ThemeProvider>
              </AntdRlUISwitcherProvider>
            </Provider>
            {isLocal && !isCypress && !isTesting ? (
              <LazyReactQueryDevtools position="bottom-right" />
            ) : null}
          </QueryClientProvider>
        </CustomerPortalPreviewProvider>
      </HelmetProvider>
      <AppUpgradeConfirmationComponent />
    </ErrorBoundary>
  );
};
